import React from 'react'

import { useI18n } from '../../../configuration/contexts/I18nContext'
import { useCustomer } from '../../../configuration/hooks/useCustomer'
import { useUser } from '../../../configuration/hooks/useUser'
import styled, { FocusNoneButton } from '../../constants/theme'
import { useFontSizeToggle } from '../../hooks/useFontSizeToggle'
import { useHighContrastToggle } from '../../hooks/useHighContrastToggle'
import PreferredLanguageSelection, {
  Label,
} from '../blocks/PreferredLanguageSelection'
import { ContrastIcon, FontSizeIcon } from '../elements/Icons'

export const AccessibilityNavControls: React.FC = () => {
  const { isPatient } = useUser()
  const i18n = useI18n()
  const { isI18nEnabled } = useCustomer()
  const isPreferredLanguageEnabled = isPatient && isI18nEnabled
  const unauthenticated = isPatient === undefined
  const isLanguageDropdownVisible =
    unauthenticated || isPreferredLanguageEnabled

  return (
    <Wrapper>
      {isLanguageDropdownVisible && (
        <div>
          <Label htmlFor="nav-preferred-language">
            {i18n.t('preferredLanguage')}
          </Label>
          <PreferredLanguageSelection id="nav-preferred-language" />
        </div>
      )}
      <HighContrastButton />
      <FontSizeButton />
    </Wrapper>
  )
}

const HighContrastButton = () => {
  const { contrastTitle, isHighContrast, handleToggleHighContrast } =
    useHighContrastToggle()
  return (
    <FocusNoneButton
      aria-live="polite"
      className={isHighContrast ? 'active' : undefined}
      onClick={handleToggleHighContrast}
      title={contrastTitle}
    >
      <ContrastSpan tabIndex={-1}>
        <ContrastIcon />
      </ContrastSpan>
    </FocusNoneButton>
  )
}

const FontSizeButton = () => {
  const { fontSizeTitle, handleToggleFontSize } = useFontSizeToggle()
  return (
    <FocusNoneButton
      aria-live="polite"
      onClick={handleToggleFontSize}
      title={fontSizeTitle}
    >
      <FontSizeSpan tabIndex={-1}>
        <FontSizeIcon />
      </FontSizeSpan>
    </FocusNoneButton>
  )
}

const Wrapper = styled.div({
  display: 'flex',
  alignItems: 'center',
})

const ContrastSpan = styled.span(
  {
    '& svg': {
      marginTop: 5,
    },
    '&:focus': {
      border: 'none',
      outline: 'none',
    },
    'button.active & svg': {
      transform: 'scaleX(-1)',
    },
    padding: 10,
  },
  ({ theme }) => ({
    'button:focus &': {
      border: `2px groove ${theme.primary}`,
      borderRadius: theme.borderRadiusSmall,
    },
  }),
)

const FontSizeSpan = styled.span(
  {
    '&:focus': {
      border: 'none',
      outline: 'none',
    },
    padding: '12px 2px',
  },
  ({ theme }) => ({
    'button:focus &': {
      border: `2px groove ${theme.primary}`,
      borderRadius: theme.borderRadiusSmall,
    },
  }),
)
